import type { Identifier, RaRecord } from "ra-core";
import type { Person } from "@domain/model/Person";
import type { Client } from "@/domain/model/Client";
import type { ConstructionSite } from "@/domain/model/ConstructionSite";
import type { Job, JobState } from "@domain/model/Job";
import type { Address } from "@domain/model/Address";
import type { Assignee } from "@/domain/model/Assignee";

const asId = (x: unknown): Identifier => String(x) as Identifier;
const iso = (d: Date | string) => (d instanceof Date ? d.toISOString() : d);

export type PersonRaRecord = RaRecord & {
  id: Identifier;
  displayName: string;
  preferredName: string | null;
  mobilePhoneE164: string | null;
  email: string | null;
  canAppearInPhoto: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type ClientRaRecord = RaRecord & {
  id: Identifier;
  name: string;
  addressId: Identifier;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  version: number;
  createdAt: string; // ISO
  updatedAt: string; // ISO
  deletedAt: string | null; // ISO | null
};

export type ConstructionSiteRaRecord = RaRecord & {
  id: Identifier;
  clientId: Identifier;
  addressId: Identifier | null;
  label: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type JobRaRecord = RaRecord & {
  id: Identifier;
  title: string;
  constructionSiteId: Identifier;
  description: string | null;
  notes: string | null;
  plannedStartAt: string | null;
  assignedPersonId: Identifier | null;
  jobState: JobState;
  lastEventAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type AddressRaRecord = RaRecord & {
  id: Identifier;
  label: string | null;
  street: string;
  streetNumber: string;
  city: string;
  zip: string;
  country: string | null;
  lat: number | null;
  lon: number | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type AssigneeRaRecord = RaRecord & {
  id: Identifier;
  displayName: string;
  preferredName: string | null;
};

export const toRaPerson = (p: Person): PersonRaRecord => ({
  id: asId(p.id),
  displayName: p.displayName,
  preferredName: p.preferredName ?? null,
  mobilePhoneE164: p.mobilePhoneE164 ?? null,
  email: p.email ?? null,
  canAppearInPhoto: !!p.canAppearInPhoto,
  version: p.version,
  createdAt: iso(p.createdAt),
  updatedAt: iso(p.updatedAt),
  deletedAt: p.deletedAt ? iso(p.deletedAt) : null,
});

export const toRaClient = (c: Client): ClientRaRecord => ({
  id: asId(c.id),
  name: c.name,
  addressId: asId(c.addressId),
  contactName: c.contactName ?? null,
  phone: c.phone ?? null,
  email: c.email ?? null,
  version: c.version,
  createdAt: iso(c.createdAt),
  updatedAt: iso(c.updatedAt),
  deletedAt: c.deletedAt ? iso(c.deletedAt) : null,
});

export const toRaConstructionSite = (
  c: ConstructionSite,
): ConstructionSiteRaRecord => ({
  id: asId(c.id),
  clientId: asId(c.clientId),
  addressId: c.addressId ? asId(c.addressId) : null,
  label: c.label ?? null,
  version: c.version,
  createdAt: iso(c.createdAt),
  updatedAt: iso(c.updatedAt),
  deletedAt: c.deletedAt ? iso(c.deletedAt) : null,
});

export const toRaJob = (j: Job): JobRaRecord => ({
  id: asId(j.id),
  title: j.title,
  constructionSiteId: asId(j.constructionSiteId),
  description: j.description ?? null,
  notes: j.notes ?? null,
  plannedStartAt: j.plannedStartAt ? iso(j.plannedStartAt) : null,
  assignedPersonId: j.assignedPersonId ?? null,
  jobState: j.jobState,
  lastEventAt: j.lastEventAt ? iso(j.lastEventAt) : null,
  version: j.version,
  createdAt: iso(j.createdAt),
  updatedAt: iso(j.updatedAt),
  deletedAt: j.deletedAt ? iso(j.deletedAt) : null,
});

export const toRaAddress = (a: Address): AddressRaRecord => ({
  id: asId(a.id),
  label: a.label ?? null,
  street: a.street,
  streetNumber: a.streetNumber,
  city: a.city,
  zip: a.zip,
  country: a.country ?? null,
  lat: a.lat ?? null,
  lon: a.lon ?? null,
  version: a.version,
  createdAt: iso(a.createdAt),
  updatedAt: iso(a.updatedAt),
  deletedAt: a.deletedAt ? iso(a.deletedAt) : null,
});

export const toRaAssignee = (a: Assignee): AssigneeRaRecord => ({
  id: asId(a.id),
  displayName: a.displayName,
  preferredName: a.preferredName ?? null,
});
